// src/utils/helpers.js - ThryveLoop v5.0 Helper Functions

/**
 * Date & Time Helpers
 */

export const formatDate = (date) => {
  const d = new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (date) => {
  const d = new Date(date);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getDayOfWeek = (date) => {
  const d = new Date(date);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[d.getDay()];
};

export const getDaysUntil = (endDate) => {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

export const getDateRange = (range) => {
  const end = new Date();
  const start = new Date();

  switch (range) {
    case 'week':
      start.setDate(end.getDate() - 7);
      break;
    case 'twoweeks':
      start.setDate(end.getDate() - 14);
      break;
    case 'month':
      start.setDate(end.getDate() - 30);
      break;
    default:
      start.setDate(end.getDate() - 7);
  }

  return { start, end };
};

/**
 * Data Formatting Helpers
 */

export const formatEnergy = (value) => {
  if (!value && value !== 0) return '--';
  return `${Math.round(value)}%`;
};

export const formatNumber = (value) => {
  if (!value && value !== 0) return '--';
  return Math.round(value).toLocaleString();
};

/**
 * Data Analysis Helpers
 */

export const getAverageEnergy = (observations) => {
  if (!observations || observations.length === 0) return 0;
  const sum = observations.reduce((acc, obs) => acc + (obs.energy || 0), 0);
  return Math.round(sum / observations.length);
};

export const getEnergyStats = (observations) => {
  if (!observations || observations.length === 0) {
    return { avg: 0, high: 0, low: 0, trend: 'stable' };
  }

  const energies = observations.map(o => o.energy || 0);
  const avg = Math.round(energies.reduce((a, b) => a + b, 0) / energies.length);
  const high = Math.max(...energies);
  const low = Math.min(...energies);

  const recent = observations.slice(-5).map(o => o.energy || 0);
  const trend = recent.length >= 2 && recent[recent.length - 1] > recent[0] ? 'up' : 'down';

  return { avg, high, low, trend };
};

export const getActivityBreakdown = (observations, limit = 5) => {
  if (!observations || observations.length === 0) return [];

  const activities = {};
  observations.forEach(obs => {
    if (obs.activity) {
      const words = obs.activity.split(' ').slice(0, 3).join(' ');
      activities[words] = (activities[words] || 0) + 1;
    }
  });

  return Object.entries(activities)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([activity, count]) => ({ activity, count }));
};

/**
 * Experiment Helpers
 */

export const ACTIVE_EXPERIMENT_STATUSES = ['active', 'continuing', 'iterating'];

const normalizeStatus = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value.trim().toLowerCase();
};

const SIDEBAR_EXPERIMENT_ID_KEYS = [
  'id',
  'pocketbaseId',
  'localId',
  '_id',
  'uuid',
  'tempId'
];

const normalizeIdentityPart = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
};

export const getExperimentIdentity = (experiment) => {
  if (!experiment || typeof experiment !== 'object') {
    return '';
  }

  for (const key of SIDEBAR_EXPERIMENT_ID_KEYS) {
    const value = normalizeIdentityPart(experiment[key]);
    if (value) {
      return `${key}:${value}`;
    }
  }

  const fallbackParts = [
    normalizeStatus(experiment.status),
    normalizeIdentityPart(experiment.start),
    normalizeIdentityPart(experiment.end),
    normalizeIdentityPart(experiment.createdAt || experiment.created),
    normalizeIdentityPart(experiment.curiosity || experiment.action)
  ].filter(Boolean);

  return fallbackParts.length ? `fallback:${fallbackParts.join('|')}` : '';
};

const parseCycleValue = (value) => {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const isExperimentActive = (experimentOrStatus) => {
  const status = typeof experimentOrStatus === 'string'
    ? experimentOrStatus
    : experimentOrStatus?.status;
  if (!status) {
    // Legacy experiments without an explicit status are treated as active
    return true;
  }
  return ACTIVE_EXPERIMENT_STATUSES.includes(normalizeStatus(status));
};

export const isExperimentReadOnly = (experimentOrStatus) => {
  const status = typeof experimentOrStatus === 'string'
    ? experimentOrStatus
    : experimentOrStatus?.status;
  const normalized = normalizeStatus(status);
  return normalized === 'completed' || normalized === 'stopped';
};

export const getExperimentLifecycleLabel = (experiment) => {
  const status = normalizeStatus(experiment?.status);
  if (status === 'continuing') return 'Continuing';
  if (status === 'iterating') return 'Iterating';
  if (status === 'paused') return 'Paused';
  if (status === 'stopped') return 'Stopped';
  if (status === 'completed') return 'Completed';
  return 'In Progress';
};

export const isExperimentReadyForReview = (experiment) => {
  if (!experiment || !experiment.end) {
    return false;
  }
  try {
    const end = new Date(experiment.end);
    if (Number.isNaN(end.getTime())) return false;
    const now = new Date();
    const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diffDays <= 0 && (isExperimentActive(experiment) || normalizeStatus(experiment.status) === 'continuing' || normalizeStatus(experiment.status) === 'iterating');
  } catch {
    return false;
  }
};

export const getExperimentCycleDepth = (experiment) => {
  if (!experiment) return 0;
  const history = Array.isArray(experiment.lifecycleHistory) ? experiment.lifecycleHistory : [];
  const historyDepth = history.reduce((max, entry) => {
    const parsed = parseCycleValue(entry?.cycle);
    return Math.max(max, parsed || 0);
  }, 0);
  if (historyDepth > 0) {
    return historyDepth;
  }
  const parsedCounter = parseCycleValue(experiment.cycleCount);
  if (parsedCounter && parsedCounter > 0) {
    return Math.max(parsedCounter - 1, 0);
  }
  const reviewLength = Array.isArray(experiment.reviewHistory)
    ? experiment.reviewHistory.length
    : (experiment.lastReview ? 1 : 0);
  return reviewLength;
};

export const getExperimentCycleDisplayNumber = (experiment) => {
  const depth = getExperimentCycleDepth(experiment);
  return Math.max(1, depth || 1);
};

export const computeExperimentLifecycle = (experiments = []) => {
  const readiness = {
    readyExperiments: [],
    continuingExperiments: [],
    iteratingExperiments: [],
    completedExperiments: [],
    activeCount: 0
  };
  const cycleSummaries = {
    continuingCycles: 0,
    iteratingCycles: 0
  };

  experiments.forEach(exp => {
    if (!exp) return;
    const normalizedStatus = normalizeStatus(exp.status);
    const normalizedReviewState = normalizeStatus(exp.reviewState);
    const isContinuing = normalizedStatus === 'continuing' || normalizedReviewState === 'continuing';
    const isIterating = normalizedStatus === 'iterating' || normalizedReviewState === 'iterating';
    const lifecycleHistory = Array.isArray(exp.lifecycleHistory) ? exp.lifecycleHistory : [];

    lifecycleHistory.forEach(entry => {
      const decision = normalizeStatus(entry?.decision);
      if (decision === 'continue') {
        cycleSummaries.continuingCycles += 1;
      } else if (decision === 'iterate') {
        cycleSummaries.iteratingCycles += 1;
      }
    });

    if (isExperimentActive(exp)) {
      readiness.activeCount += 1;
    }
    if (isExperimentReadyForReview(exp)) {
      readiness.readyExperiments.push(exp);
    }
    if (isContinuing) {
      readiness.continuingExperiments.push(exp);
    }
    if (isIterating) {
      readiness.iteratingExperiments.push(exp);
    }
    if (normalizedStatus === 'completed') {
      readiness.completedExperiments.push(exp);
    }
  });

  return {
    readyCount: readiness.readyExperiments.length,
    continuingCount: readiness.continuingExperiments.length,
    iteratingCount: readiness.iteratingExperiments.length,
    completedCount: readiness.completedExperiments.length,
    activeCount: readiness.activeCount,
    cycleDepth: {
      max: experiments.reduce((max, exp) => Math.max(max, getExperimentCycleDepth(exp)), 0),
      continuing: readiness.continuingExperiments.reduce((max, exp) => Math.max(max, getExperimentCycleDepth(exp)), 0),
      iterating: readiness.iteratingExperiments.reduce((max, exp) => Math.max(max, getExperimentCycleDepth(exp)), 0)
    },
    totalContinuingCycles: cycleSummaries.continuingCycles,
    totalIteratingCycles: cycleSummaries.iteratingCycles,
    continuingReadyCount: readiness.continuingExperiments.filter(exp => isExperimentReadyForReview(exp)).length,
    iteratingReadyCount: readiness.iteratingExperiments.filter(exp => isExperimentReadyForReview(exp)).length,
    ...readiness
  };
};

export const computeSidebarBadgeCounts = ({
  experiments = [],
  lifecycleStats = {},
  observationsCount = 0,
  journalCount = 0
} = {}) => {
  const safeExperiments = Array.isArray(experiments) ? experiments : [];
  const nonCompletedExperiments = safeExperiments.filter((exp) => normalizeStatus(exp?.status) !== 'completed').length;

  const lifecycleLanes = [
    lifecycleStats?.readyExperiments,
    lifecycleStats?.continuingExperiments,
    lifecycleStats?.iteratingExperiments,
    lifecycleStats?.completedExperiments
  ];

  const decideIds = new Set();
  let laneFallbackTotal = 0;

  lifecycleLanes.forEach((lane, laneIndex) => {
    if (!Array.isArray(lane)) return;

    laneFallbackTotal += lane.length;
    lane.forEach((exp, expIndex) => {
      const identity = getExperimentIdentity(exp);
      if (identity) {
        decideIds.add(identity);
        return;
      }
      decideIds.add(`lane-${laneIndex}-index-${expIndex}`);
    });
  });

  const readyCount = Number.isFinite(lifecycleStats?.readyCount)
    ? lifecycleStats.readyCount
    : (Array.isArray(lifecycleStats?.readyExperiments) ? lifecycleStats.readyExperiments.length : 0);

  return {
    observations: Math.max(0, Number(observationsCount) || 0),
    experiments: nonCompletedExperiments,
    review: decideIds.size > 0 ? decideIds.size : laneFallbackTotal,
    reviewPending: Math.max(0, Number(readyCount) || 0),
    journal: Math.max(0, Number(journalCount) || 0)
  };
};

export const getExperimentProgress = (experiment) => {
  const start = new Date(experiment.start);
  const end = new Date(experiment.end);
  const now = new Date();

  const totalDays = (end - start) / (1000 * 60 * 60 * 24);
  const elapsedDays = (now - start) / (1000 * 60 * 60 * 24);

  const progress = Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100));

  return {
    progress: Math.round(progress),
    daysLeft: Math.ceil((end - now) / (1000 * 60 * 60 * 24)),
    isComplete: now >= end
  };
};

export const getExperimentStatus = (experiment) => {
  const daysLeft = getDaysUntil(experiment.end);
  const status = normalizeStatus(experiment.status);

  if (status === 'completed') {
    return 'Completed';
  }
  if (status === 'paused') {
    return 'Paused';
  }
  if (status === 'stopped') {
    return 'Stopped';
  }
  if (status === 'continuing') {
    return daysLeft <= 0 ? 'Ready for follow-up review' : 'Continuing';
  }
  if (status === 'iterating') {
    return daysLeft <= 0 ? 'Iteration ready to review' : 'Iterating';
  }
  if (daysLeft <= 0) {
    return 'Ready to review';
  }
  return `${daysLeft} days left`;
};

/**
 * Validation Helpers
 */

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const isValidURL = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateObservation = (obs) => {
  const errors = [];

  if (!obs.activity || obs.activity.trim().length === 0) {
    errors.push('Activity description is required');
  }

  if (obs.energy === null || obs.energy === undefined) {
    errors.push('Energy level is required');
  } else if (obs.energy < 0 || obs.energy > 100) {
    errors.push('Energy must be between 0 and 100');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateExperiment = (exp) => {
  const errors = [];

  if (!exp.curiosity || exp.curiosity.trim().length === 0) {
    errors.push('Curiosity question is required');
  }

  if (!exp.action || exp.action.trim().length === 0) {
    errors.push('Action description is required');
  }

  if (!exp.signal || exp.signal.trim().length === 0) {
    errors.push('Success signal is required');
  }

  if (!exp.days || exp.days < 3 || exp.days > 90) {
    errors.push('Duration must be between 3 and 90 days');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * String Helpers
 */

export const truncate = (str, length = 50) => {
  if (!str) return '';
  return str.length > length ? str.substring(0, length) + '...' : str;
};

export const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const slugify = (str) => {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');
};

/**
 * Array Helpers
 */

export const chunk = (arr, size) => {
  const chunked = [];
  for (let i = 0; i < arr.length; i += size) {
    chunked.push(arr.slice(i, i + size));
  }
  return chunked;
};

export const uniq = (arr) => {
  return [...new Set(arr)];
};

export const groupBy = (arr, key) => {
  return arr.reduce((result, item) => {
    const group = item[key];
    if (!result[group]) result[group] = [];
    result[group].push(item);
    return result;
  }, {});
};

/**
 * Storage Helpers
 */

export const getFromStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const setInStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
};

export const removeFromStorage = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

/**
 * Download Helper
 */

export const downloadJSON = (data, filename = 'data.json') => {
  const dataStr = JSON.stringify(data, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

export const downloadCSV = (data, filename = 'data.csv') => {
  if (!data || data.length === 0) {
    console.error('No data to export');
    return;
  }

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row => headers.map(h => JSON.stringify(row[h] || '')).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};

/**
 * Mobile Detection
 */

export const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth <= 768;
};

export const isTablet = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth > 768 && window.innerWidth <= 1024;
};

export const isDesktop = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth > 1024;
};

export default {
  // Date & Time
  formatDate,
  formatDateTime,
  getDayOfWeek,
  getDaysUntil,
  getDateRange,

  // Data Formatting
  formatEnergy,
  formatNumber,

  // Analysis
  getAverageEnergy,
  getEnergyStats,
  getActivityBreakdown,

  // Experiments
  ACTIVE_EXPERIMENT_STATUSES,
  isExperimentActive,
  isExperimentReadyForReview,
  computeExperimentLifecycle,
  isExperimentReadOnly,
  getExperimentLifecycleLabel,
  getExperimentProgress,
  getExperimentStatus,

  // Validation
  isValidEmail,
  isValidURL,
  validateObservation,
  validateExperiment,

  // Strings
  truncate,
  capitalize,
  slugify,

  // Arrays
  chunk,
  uniq,
  groupBy,

  // Storage
  getFromStorage,
  setInStorage,
  removeFromStorage,

  // Download
  downloadJSON,
  downloadCSV,

  // Mobile
  isMobile,
  isTablet,
  isDesktop
};
